import { useEffect, useRef, useState } from 'react';
import classes from './Dropbox.module.css';
import {
  Box,
  BoxProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { IMAGE_MAP_DROP } from '../../utils/imageMap';
import { useClaimPetMutation } from '../../generated/graphql';
import { useStationAuth } from '../../contexts/StationAuthContext';

type Pet = {
  id: string;
  name: string;
  type: string;
  rarity: string;
  img: string;
};

type Props = BoxProps & {
  pet: any;
  rarity: 'common' | 'rare' | 'legendary';
  shouldOpen: boolean;
  onReady?: () => void;
  onOpened: () => void;
};

export default function DropBox(props: Props) {
  const { pet, shouldOpen, rarity, onReady, sx, ...rest } = props;
  const { playerId } = useStationAuth();
  const ref = useRef<HTMLDivElement>(null);
  const [boxState, setBoxState] = useState<
    'opened' | 'appearing' | 'appeared' | 'opening'
  >('appearing');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [{ data: claimPetData }, claimPet] = useClaimPetMutation();

  const wooshSound = useRef<HTMLAudioElement | null>(null);
  const boxAppearSound = useRef<HTMLAudioElement | null>(null);
  const boxOpeningSound = useRef<HTMLAudioElement | null>(null);
  const petAppearSound = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    wooshSound.current = new Audio('/sounds/drops/woosh.wav');
    boxAppearSound.current = new Audio('/sounds/drops/box_appeared.wav');
    boxOpeningSound.current = new Audio('/sounds/drops/boxshake.wav');
    petAppearSound.current = new Audio('/sounds/drops/pet_appear.wav');

    wooshSound.current.volume = 1;
    boxAppearSound.current.volume = 1;
    boxOpeningSound.current.volume = 1;
    petAppearSound.current.volume = 1;
  }, []);

  useEffect(() => {
    if (boxState === 'appearing') {
      wooshSound.current?.play();

      const delay = setTimeout(() => {
        boxAppearSound.current?.play();
      }, 1000);

      return () => clearTimeout(delay);
    }
  }, [boxState]);

  useEffect(() => {
    if (shouldOpen) {
      setBoxState('opening');
      boxOpeningSound.current?.play();
    }
  }, [shouldOpen]);

  useEffect(() => {
    (async () => {
      try {
        await claimPet({
          playerId: playerId || '',
          petId: pet?.id || '',
        });
        sessionStorage.setItem('newPet', pet?.id);
      } catch (err) {
        console.error('claimPet error', err);
      }
    })();
  }, [rarity, claimPet]);

  const isShowing = boxState === 'appearing';
  const isOpening = boxState === 'opening';
  const isShown = boxState === 'appeared';
  const isOpened = boxState === 'opened';

  const isShownOrOpened = isShown || isOpened;

  let bgImageUrl = '';
  type Rarity = 'common' | 'rare' | 'legendary';
  type DropOpenKey = `drop-open-${Rarity}`;

  if (isShowing) {
    bgImageUrl = `url(${IMAGE_MAP_DROP['drop-appear']})`;
  } else if (isOpening) {
    const exactRarity = rarity.toLowerCase() as Rarity;
    const key = `drop-open-${exactRarity}` as DropOpenKey;
    bgImageUrl = `url(${IMAGE_MAP_DROP[key]})`;
  }

  let animationClassName: string | undefined;
  if (isShowing || isShown) {
    animationClassName = classes['drop-box-appearing'];
  } else if (isOpening || isOpened) {
    animationClassName = classes['drop-box-opening'];
  }

  useEffect(() => {
    function updateWidth() {
      const width = ref.current?.offsetWidth;
      ref.current?.style.setProperty('--current-width', `${width}px`);
    }

    updateWidth();

    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (shouldOpen) {
      setBoxState('opening');
    }
  }, [shouldOpen]);

  return (
    <Box
      component="div"
      ref={ref}
      sx={{
        maxWidth: '500px',
        aspectRatio: '1 / 1',
        backgroundImage: bgImageUrl,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        ...sx,
      }}
      onAnimationEnd={() => {
        if (boxState === 'appearing') {
          setBoxState('appeared');
          boxAppearSound.current?.play();
          onReady?.();
        } else if (boxState === 'opening') {
          setBoxState('opened');
          petAppearSound.current?.play();
          props.onOpened();
        }
      }}
      className={animationClassName}
      {...rest}
    >
      <Box
        sx={{
          opacity: isShownOrOpened ? 1 : 0,
          position: 'relative',
        }}
      >
        {isOpened && (
          <>
            {(() => {
              const getPalette = (r: 'common' | 'rare' | 'legendary') => {
                switch (r) {
                  case 'rare':
                    return {
                      background: '#D34DFF',
                      rarityBg: '#B000DD',
                      strokeCard: '#FF7BF6',
                      strokeNameBg: 'rgba(255,123,246,0.7)',
                      outerGlow: '#D65CFF',
                      innerGlow: '#FF4DCA',
                    };
                  case 'legendary':
                    return {
                      background: '#FFD900',
                      rarityBg: '#D49F00',
                      strokeCard: '#FFD900',
                      strokeNameBg: 'rgba(255,217,0,0.7)',
                      outerGlow: '#FFC300',
                      innerGlow: '#FF9D00',
                    };
                  case 'common':
                  default:
                    return {
                      background: '#406CFF',
                      rarityBg: '#164BDD',
                      strokeCard: '#7BB7FF',
                      strokeNameBg: 'rgba(123,183,255,0.7)',
                      outerGlow: '#008CFF',
                      innerGlow: '#00B2FF',
                    };
                }
              };

              const palette = getPalette(rarity.toLowerCase() as Rarity);
              const strokeCardWidth = 4;
              const strokeNameWidth = 4;
              const rounded = 24;

              return (
                <>
                  <Box
                    sx={{
                      width: '100%',
                      height: isMobile ? '35vh' : '500px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      pointerEvents: 'none',
                      userSelect: 'none',
                      position: 'relative',
                      zIndex: 1100,
                      transformOrigin: 'center',
                      animation:
                        'flyInCenter 700ms cubic-bezier(0.2,0.9,0.2,1) both',
                      willChange: 'transform, opacity, filter',
                      '@keyframes flyInCenter': {
                        '0%': {
                          transform: 'translateY(-1.6rem) scale(0.75)',
                          opacity: 0,
                          filter: 'blur(3px)',
                        },
                        '60%': {
                          transform: 'translateY(-0.4rem) scale(1.04)',
                          opacity: 1,
                          filter: 'none',
                        },
                        '100%': {
                          transform: 'translateY(0) scale(1)',
                          opacity: 1,
                          filter: 'none',
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: palette.rarityBg,
                        borderRadius: '12px',
                        position: 'absolute',
                        top: '-0.2rem',
                        left: '50%',
                        transform:
                          'translateX(-50%) translateY(-0.2rem) scale(1)',
                        padding: '7px 10px',
                        width: '200px',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        zIndex: 1200,
                        justifyContent: 'center',
                        alignItems: 'center',
                        pointerEvents: 'none',
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                          color: '#fff',
                          fontSize: '1.2rem',
                          textAlign: 'center',
                        }}
                      >
                        {rarity[0].toUpperCase() +
                          rarity.substring(1).toLowerCase()}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '92%',
                        height: '92%',
                        borderRadius: `${rounded}px`,
                        background: palette.strokeCard,
                        padding: `${strokeCardWidth}px`,
                        boxShadow: `0 8px 30px ${palette.outerGlow}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        animation: 'pulseGlow 2200ms ease-in-out infinite',
                        '@keyframes pulseGlow': {
                          '0%': {
                            boxShadow: `0 8px 20px ${palette.outerGlow}`,
                          },
                          '50%': {
                            boxShadow: `0 8px 50px ${palette.outerGlow}`,
                          },
                          '100%': {
                            boxShadow: `0 8px 20px ${palette.outerGlow}`,
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          borderRadius: `${rounded - strokeCardWidth}px`,
                          background: palette.background,
                          position: 'relative',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: `inset 0 0 60px ${palette.innerGlow}`,
                          animation: 'innerPulse 2200ms ease-in-out infinite',
                          '@keyframes innerPulse': {
                            '0%': {
                              boxShadow: `inset 0 0 30px ${palette.innerGlow}`,
                            },
                            '50%': {
                              boxShadow: `inset 0 0 90px ${palette.innerGlow}`,
                            },
                            '100%': {
                              boxShadow: `inset 0 0 30px ${palette.innerGlow}`,
                            },
                          },
                        }}
                      >
                        {/* <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            color: '#FFFFFF',
                            fontSize: '4vw',
                            textShadow: `2px 2px 4px rgba(0,0,0,0.6)`,
                          }}
                        /> */}
                      </Box>

                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: '0rem',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: '#FFFFFF',
                          padding: '8px 16px',
                          borderRadius: `${rounded}px`,
                          border: `${strokeNameWidth}px solid ${palette.strokeNameBg}`,
                          fontSize: '20px',
                          fontWeight: '700',
                          minWidth: '200px',
                          color: '#000',
                          whiteSpace: 'nowrap',
                          textAlign: 'center',
                          pointerEvents: 'none',
                          zIndex: 5,
                        }}
                      >
                        {pet?.name ? pet.name : 'Unknown'}
                      </Box>
                    </Box>
                  </Box>
                </>
              );
            })()}
          </>
        )}
        {/* static image */}
        <Box
          component="img"
          draggable="false"
          src={
            isShowing || isShown
              ? IMAGE_MAP_DROP['drop-static']
              : isOpening || isOpened
              ? pet?.img
              : ''
          }
          sx={{
            width: '100%',
            display: 'block',
            position: 'absolute',
            zIndex: 1210,
            padding: isOpened ? '1rem' : '0%',
            top: 0,
          }}
        />
        <Box sx={{ height: '150px' }}></Box>
      </Box>
    </Box>
  );
}
